import React from 'react';
import { motion } from 'framer-motion';

// Placeholder Icon
const ArrowRightIcon = () => (
  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" className="ml-2 group-hover/link:translate-x-1 transition-transform" height="14" width="14" xmlns="http://www.w3.org/2000/svg"><path d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path></svg>
);

const solutionsData = [
  {
    imgSrc: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    alt: "Salesforce CRM Implementation",
    title: "Salesforce CRM Implementation",
    description: "Implement and customize Salesforce CRM systems with industry-specific solutions tailored to your unique business requirements."
  },
  {
    imgSrc: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    alt: "SAP ERP Solutions",
    title: "SAP ERP Solutions",
    description: "Streamline operations with integrated SAP ERP solutions that optimize your business processes and enhance operational efficiency."
  },
  {
    imgSrc: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    alt: "Digital Transformation",
    title: "Digital Transformation",
    description: "Complete end-to-end digital transformation strategies to modernize your business and stay competitive in the rapidly evolving digital landscape."
  },
  {
    imgSrc: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    alt: "Cloud Migration Services",
    title: "Cloud Migration Services",
    description: "Seamlessly migrate your legacy systems to cloud platforms with our expert guidance ensuring minimal disruption to your operations."
  }
];

const stats = [
    { value: "100+", label: "Projects Delivered" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "50+", label: "Expert Consultants" },
    { value: "24/7", label: "Customer Support" }
];

function Solutions() {

    const sectionVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const gridContainerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
    };

    const gridItemVariants = {
        hidden: { opacity: 0, y: 20, scale: 0.95 },
        visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } }
    };

    const statsContainerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
    };

    const statItemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

  return (
    <section id="solutions" className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={sectionVariants}
        >
          <span className="text-primary font-semibold tracking-wider uppercase">What We Offer</span>
          <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">Enterprise Solutions</h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive enterprise solutions designed to solve real business challenges and accelerate digital growth
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <motion.div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8"
            variants={gridContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
        >
          {solutionsData.map((solution, index) => (
            <motion.div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 group"
                variants={gridItemVariants}
            >
              <div className="relative overflow-hidden h-56">
                <img
                  src={solution.imgSrc}
                  alt={solution.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{solution.title}</h3>
                <p className="text-gray-600 mb-5 text-sm">{solution.description}</p>
                <a href="#contact" className="inline-flex items-center text-primary font-medium hover:text-blue-700 group/link text-sm">
                  Learn more
                  <ArrowRightIcon />
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section */}
        <motion.div
            className="mt-24 p-8 bg-white rounded-2xl shadow-lg"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={statsContainerVariants}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
                <motion.div key={index} className="text-center" variants={statItemVariants}>
                    <h3 className="text-4xl font-bold text-primary mb-2">{stat.value}</h3>
                    <p className="text-gray-600 text-sm">{stat.label}</p>
                </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Solutions; 